import { clearIdpConnectReturn } from "@/helpers/idpConnect";
import { User } from "../types";
import { getTenant, isSSOEnabled } from "../helpers/tenant";
import { setFreemiumMode } from "../helpers/freemiumMode";
import { posthogIdentify, sentrySetUser } from "./logging";
import { setAuthorizationHeader } from "@/helpers/api_helper";
import { RolesEnum } from "@/helpers/constants";
import { AUTH_USER } from "@/helpers/authStorage";
import { clearDeepLinkReturn } from "@/helpers/postLoginRoute";
import { UserLoginOutResponse } from "@/helpers/types";
import { clearQuickstartEntry } from "@/pages/quickstart/quickstartEntry";

const IMPERSONATING_USER = "impersonatingUser";
const REMEMBER_ME = "rememberMe";
const LAST_LOGIN = "lastLogin";
const SSO_LAST_ACTIVE = "ssoLastActive";

const REMEMBER_DURATION = 6 * 60 * 60 * 1000;

export const setRememberMe = (rememberMe: boolean) => {
  localStorage.setItem(REMEMBER_ME, `${rememberMe}`);
};

export const loginUser = (user: User) => {
  saveUser(user);
  localStorage.setItem(LAST_LOGIN, `${Date.now()}`);
  posthogIdentify(user);
  sentrySetUser(user);
  setAuthorizationHeader(user.auth_token);
};

export const saveUser = (user: User) => {
  if ("role_FK" in user && user["role_FK"] && user["role_FK"]["permission"]) {
    user["permission"] = user["role_FK"]["permission"].map(item => item.name);
    // @ts-expect-error valid
    delete user["role_FK"]["permission"];
  }
  // Public admin doesn't have any permissions.
  if (getTenant() === "") user["permission"] = null;
  localStorage.setItem(AUTH_USER, JSON.stringify(user));
};

export const isSessionExpired = () => {
  // in sso mode, no need to check last login as supertokens handles session expiry
  if (isSSOEnabled()) {
    return false;
  }

  // If no user in localStorage, not expired (just no session)
  const user = localStorage.getItem(AUTH_USER);
  if (!user) {
    return false;
  }

  const rememberMe = JSON.parse(localStorage.getItem(REMEMBER_ME) || "false");
  if (rememberMe) {
    return false;
  }

  const lastLogin = localStorage.getItem(LAST_LOGIN);
  if (lastLogin && Date.now() - parseInt(lastLogin) > REMEMBER_DURATION) {
    return true;
  }

  return false;
};

export const getUser = () => {
  // Check if session is expired first
  if (isSessionExpired()) {
    logoutUser();
    return;
  }

  const user = localStorage.getItem(AUTH_USER);
  if (!user) {
    return;
  }
  const parsed_user = JSON.parse(user);
  if (!parsed_user || !("permission" in parsed_user) || !("auth_token" in parsed_user)) {
    logoutUser();
    return;
  }
  return parsed_user;
};

export const logoutUser = () => {
  // Logout is an in-app navigation, not a reload, so this module-scoped value
  // would otherwise survive and keep classifying the unauthenticated enterprise
  // page as the free stack — exposing free registration and Google sign-in.
  setFreemiumMode(false);
  // Same reason as the freemium reset above: logout is an in-app navigation, so anything
  // scoped to the tab outlives it. The deep-link stash lives in sessionStorage and is
  // popped only by `Login.onSuccess`, so a sign-in that completes elsewhere leaves it
  // behind for the next login to consume. Callers that stash after logging out — the
  // session-expiry branch in `BaseAuthProtected` — must keep doing so *after* this.
  clearDeepLinkReturn();
  clearIdpConnectReturn();
  // The Quick Start entry is origin-wide, so the next account to sign in on
  // this browser must not inherit one this account left unused.
  clearQuickstartEntry();
  try {
    localStorage.removeItem(AUTH_USER);
    localStorage.removeItem(IMPERSONATING_USER); // remove impersonation item if exists
    localStorage.removeItem(SSO_LAST_ACTIVE);
  } catch (e) {
    console.warn("[auth] Failed to clear localStorage during logout", e);
  }
};

export const updateSSOLastActive = () => {
  try {
    localStorage.setItem(SSO_LAST_ACTIVE, `${Date.now()}`);
  } catch (e) {
    // Safari private mode / disabled storage — swallow so `beforeunload` never throws.
    console.warn("[auth] Failed to persist ssoLastActive", e);
  }
};

export const getSSOLastActive = (): number | null => {
  let value: string | null;
  try {
    value = localStorage.getItem(SSO_LAST_ACTIVE);
  } catch {
    return null;
  }
  if (!value) return null;
  const parsed = parseInt(value, 10);
  // Tampered / non-numeric value — ignore rather than silently disabling the check.
  return Number.isFinite(parsed) ? parsed : null;
};

export const savePermissions = (permissions: User["permission"]) => {
  const user = getUser();
  if (user) {
    user["permission"] = permissions || [];
    saveUser(user);
  }
};

export const isAdmin = (): boolean => {
  const user = getUser();
  return Boolean(user?.role_FK?.role_title === RolesEnum.ADMIN);
};

export const isOwner = (): boolean => {
  const user = getUser();
  return Boolean(user?.role_FK?.role_title === RolesEnum.OWNER);
};

// The read-only owner has Owner-level *read* access (all teams) but cannot
// modify anything. Use this for view/visibility affordances that an Owner gets
// (e.g. the account-wide "Default" team view) — NOT for write affordances,
// which the backend blocks anyway.
export const isReadOnlyOwner = (): boolean => {
  const user = getUser();
  return Boolean(user?.role_FK?.role_title === RolesEnum.OWNER_READ_ONLY);
};

export const isServiceUser = (): boolean => {
  const user = getUser();
  return Boolean(user?.email === "serviceuser@altimate.ai");
};

export const isImpersonating = (): boolean => {
  const value = localStorage.getItem(IMPERSONATING_USER);
  return Boolean(value && value !== "null");
};

export const getImpersonationDetails = (): UserLoginOutResponse | null => {
  if (!isImpersonating()) {
    return null;
  }
  return getUser();
};

export const impersonateUser = (user: UserLoginOutResponse) => {
  const originalUser = getUser();
  let permission: string[] | undefined;
  if ("role_FK" in user && user["role_FK"] && user["role_FK"]["permission"]) {
    permission = user["role_FK"]["permission"].map(item => item.name);
    // @ts-expect-error valid
    delete user["role_FK"]["permission"];
  }
  const userToStore = permission !== undefined ? { ...user, permission } : { ...user };
  localStorage.setItem(IMPERSONATING_USER, JSON.stringify(originalUser));
  localStorage.setItem(AUTH_USER, JSON.stringify(userToStore));
  setAuthorizationHeader(user.auth_token);
  window.location.reload();
};

export const stopImpersonation = () => {
  const originalUser = localStorage.getItem(IMPERSONATING_USER);
  if (originalUser) {
    localStorage.setItem(AUTH_USER, originalUser);
  }
  localStorage.removeItem(IMPERSONATING_USER);
  window.location.reload();
};
